import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Input from '@material-ui/core/Input';
import Grid from '@material-ui/core/Grid';
import InputAdornment from '@material-ui/core/InputAdornment';

import styles from 'assets/jss/material-dashboard-pro-react/components/customInputStyle';

import _ from 'lodash';
import withHover from 'components/withHoverHOC/withHover';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IEvents {
	disabled: boolean;
	focus: boolean;
	hover: boolean;
	success: boolean;
	error: boolean;
	notEmpty: boolean;
}

function CustomInput(props: types.inputs.ICustomInput & { hideArrows?: boolean }) {
	const classes = useStyles();
	const {
		formControlProps,
		labelText,
		id,
		labelProps,
		inputProps,
		error,
		white,
		inputRootCustomClasses,
		inputLabelClasses,
		success,
		helperText,
		disabled,
		hover,
		icon,
		onInputBlur,
		variant,
		disableHover,
		inputCustomClasses,
		hideArrows,
		isNewDashBoardEnabled = false,
	} = props;

	const { onFocus, onBlur, value } = inputProps || {};

	const [focusState, setFocusState] = useState<boolean>(false);

	/*
    Prepares a ranking of the events.
    The list is prepared from the most important events that preceed others.
    At most one element can be true.
  */
	const eventsRanking: IEvents = [
		['disabled', !!disabled],
		['focus', focusState],
		['hover', hover && !disableHover],
		['success', !!success],
		['error', !!error],
		['notEmpty', !!value && value.length > 0],
	].reduce(
		([ranks, hasRendered], [name, value]) => [
			{
				...ranks,
				[String(name)]: !hasRendered && value,
			},
			hasRendered || value,
		],
		[{}, false]
	)[0] as IEvents;

	useEffect(() => {
		setFocusState(false);
	}, [disabled]);

	const labelClasses = classNames({
		[classes.labelDisabled]: eventsRanking.disabled,
		[classes.labelHover]: eventsRanking.hover,
		[String(inputLabelClasses)]: true,
	});

	const underlineClasses = classNames({
		[classes.underline]: true,
		[classes.underlineError]: error,
		[classes.underlineSuccess]: eventsRanking.success,
		[classes.underlineNotEmpty]: eventsRanking.notEmpty,
		[classes.whiteUnderline]: white,
	});
	const marginTop = classNames({
		[String(inputRootCustomClasses)]: inputRootCustomClasses !== undefined,
	});
	const inputClasses = classNames({
		[String(inputCustomClasses)]: !!inputCustomClasses,
		[classes.input]: true,
		[classes.whiteInput]: white,
		[classes.hideArrows]: hideArrows,
	});
	var formControlClasses;

	const formControlBaseClasses = classNames({
		[classes.formControlHover]: eventsRanking.hover,
		[classes.formControlSuccess]: eventsRanking.success,
		[classes.formControlError]: eventsRanking.error,
		[classes.formControlNotEmpty]: eventsRanking.notEmpty,
		[classes.formControlFocus]: eventsRanking.focus,
		[classes.formControlIcon]: !!icon,
		[classes.newThemedInput]: isNewDashBoardEnabled,
	});

	if (formControlProps !== undefined) {
		formControlClasses = classNames(formControlProps.className, classes.formControl);
	} else {
		formControlClasses = classes.formControl;
	}

	formControlClasses = formControlClasses + ' ' + formControlBaseClasses;

	var helpTextClasses = classNames({
		[classes.helperText]: true,
		[classes.helperTextError]: error,
		[classes.helperTextSuccess]: eventsRanking.success,
		[classes.helperTextDisabled]: eventsRanking.disabled,
	});

	const iconClasses = classNames({
		[classes.icon]: true,
		[classes.iconSuccess]: eventsRanking.success,
		[classes.iconError]: eventsRanking.error,
		[classes.iconHover]: eventsRanking.hover,
		[classes.iconFocus]: eventsRanking.focus,
	});

	const VariantInput = variant === 'outlined' ? OutlinedInput : Input;

	return (
		<Grid container className={classes.inputWrapper} id={id}>
			{icon && <div className={iconClasses}>{icon}</div>}
			<FormControl {...formControlProps} className={formControlClasses}>
				{labelText !== undefined ? (
					<InputLabel className={classes.labelRoot + ' ' + labelClasses} htmlFor={id} {...labelProps}>
						{labelText}
					</InputLabel>
				) : null}
				<VariantInput
					classes={{
						input: inputClasses,
						root: marginTop,
						disabled: classes.disabled,
						...(variant === 'outlined' ? {} : { underline: underlineClasses }),
					}}
					id={id}
					disabled={disabled}
					onFocus={(event) => {
						setFocusState(true);
						return onFocus && onFocus(event);
					}}
					onBlur={(event) => {
						setFocusState(false);
						if (onInputBlur) {
							onInputBlur();
						}
						return onBlur && onBlur(event);
					}}
					inputProps={_.pick(inputProps, ['maxLength', 'disabled', 'autocomplete'])}
					startAdornment={
						isNewDashBoardEnabled &&
						!value && (
							<InputAdornment position="start" className={classes.placeholderAdornment}></InputAdornment>
						)
					}
					{...inputProps}
				/>
				{helperText ? (
					<FormHelperText id={id + '-text'} className={helpTextClasses}>
						{helperText}
					</FormHelperText>
				) : null}
			</FormControl>
		</Grid>
	);
}

export default withHover(CustomInput);
